import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import type { DesignerChoice } from "@/lib/designer/contracts";
import {
  CreateFormData,
  SerializableAgentMessage,
  SerializablePresetOption,
} from "@/components/create/types";
import type { CreditModalContext } from "@/components/create/creditModalContext";
import type {
  AgentActionPlan,
  AgentComposerRecovery,
  AgentPhase,
  AgentMessage,
  AgentThinkingTraceStep,
  AgentWidget,
  DesignBrief,
  DressConcept,
  PresetOption,
} from "@/types/createAgent";
import type {
  GeneratedImageVariation,
  PersistedGeneratedImageVariation,
  RenderedDesignSpec,
} from "@/types/generatedImage";
import { parseDesignSpecSnapshot } from "@/lib/agent/designSpecSnapshot";
import type { CreatePageState } from "./types";
import {
  createFormSlice,
  createGenerationSlice,
  createUISlice,
  createTicketSlice,
  createAgentSlice,
} from "./slices";
import {
  createPendingSubmittedMessageId,
  type EditableDesignDetailField,
} from "./slices/agentSlice";
import { calculateHasPrompt } from "./utils";

// Re-export for backward compatibility
export { calculateHasPrompt } from "./utils";

// Always create fresh nested state. Reusing the exported fixture inside the
// live store makes resetAll vulnerable to retaining a previous project's
// arrays after Immer has adopted those objects as a store snapshot.
export function createInitialState(): CreatePageState {
  return {
    // 1. Form & Navigation
    form: {
      data: {
        prompt: "",
        color: "",
        colorHex: "",
        length: "",
        sleeve: "",
        sleeveAddons: [],
        neckline: "",
        waistline: "",
        silhouette: "",
        fabric: "",
        generateBack: false,
        frontApproved: false,
        backApproved: false,
        backPrompt: "",
        uploadedFrontImages: [],
        uploadedBackImages: [],
        isUploadPreset: false,
        isFrontUploadPreset: false,
        isBackUploadPreset: false,
        enableCrazyMode: false,
        chatHistory: [],
        presets: [],
      },
      currentStep: 1,
      totalSteps: 4,
      hasPrompt: false,
      showValidationError: false,
      frontDesignLocked: false,
    },

    // 2. Generation
    generation: {
      images: {
        front: {
          url: null,
          path: null,
          designSpec: null,
          original: { url: null, path: null, designSpec: null },
        },
        back: {
          url: null,
          path: null,
          designSpec: null,
          original: { url: null, path: null, designSpec: null },
        },
        variations: {
          front: [],
          back: [],
        },
        selected: {
          frontIndex: null,
          backIndex: null,
        },
        isSelectingVariation: false,
      },
      progress: {
        loading: false,
        progress: 0,
        phase: "idle",
        startTime: null,
        estimatedTimeRemaining: null,
        isBlocked: false,
        cooldownTime: 0,
        hasFailedGeneration: false,
        failedSignalConsumed: false,
        lastGenerationCompletedAt: null as number | null,
      },
      errors: {
        message: null,
        warnings: [],
        partialFailure: false,
      },
    },

    // 3. UI State
    ui: {
      modification: {
        showInput: false,
        prompt: "",
        isModifying: false,
        isModificationMode: false,
        originalPresetType: null,
      },
      modals: {
        credit: {
          show: false,
          context: null,
          checkout: false,
          success: false,
          successData: null,
          config: null,
          dismissed: false,
          purchaseLoading: false,
          // Note: balance and checkLoading removed - use useCredits() hook
        },
        feedback: false,
        quoteAccount: false,
        startOverConfirm: false,
      },
      view: "front",
    },

    // 4. Ticket State
    ticket: {
      id: null,
      designSubmitted: false,
      submissionError: null,
      quoteRequiresEmailConfirmation: false,
      variationTicketIds: {},
    },

    // 5. Agent State
    agent: {
      conversation: {
        messages: [],
        isTyping: false,
        thinkingStatus: null,
        thinkingTrace: [],
        presetsLoading: false,
        error: null,
      },
      phase: "initial",
      presets: [],
      sessionId: null,
      sessionVersion: 0,
      messageVersion: 0,
      designBrief: null,
      activeConcept: null,
      actionPlan: null,
      activeRequestController: null,
      entryTransition: "idle",
      entryChatActive: false,
      pendingComposerSeed: null,
      pendingComposerRecovery: null,
      pendingSubmittedMessage: null,
      pendingSubmittedMessageId: null,
      exactSessionRestoreError: null,
    },
  };
}

// Export a stable fixture for compatibility with existing callers and tests.
// The live store and reset path intentionally use createInitialState().
export const initialState: CreatePageState = createInitialState();

function restoreGeneratedVariations(
  variations: PersistedGeneratedImageVariation[] | null | undefined
): GeneratedImageVariation[] {
  return (variations ?? []).map(variation => ({
    imageUrl: variation.imageUrl,
    imagePath: variation.imagePath ?? null,
    designSpec: parseDesignSpecSnapshot(variation.designSpec),
  }));
}

function findRenderedVariationSpec(
  variations: GeneratedImageVariation[],
  url: string | null | undefined,
  path: string | null | undefined
): RenderedDesignSpec {
  const hasUrl = typeof url === "string" && url.length > 0;
  const hasPath = typeof path === "string" && path.length > 0;
  if (!hasUrl && !hasPath) return null;

  // If persistence supplied both identifiers, they describe one exact image.
  // Requiring every supplied value avoids borrowing a snapshot from a
  // variation that merely happens to reuse a URL or storage path.
  const match = variations.find(variation => {
    const urlMatches = !hasUrl || variation.imageUrl === url;
    const pathMatches = !hasPath || variation.imagePath === path;
    return urlMatches && pathMatches;
  });
  return match?.designSpec ?? null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every(item => typeof item === "string");
}

/**
 * Save points are browser-controlled input. Rehydrate only a structurally
 * complete v2 brief so a malformed local payload cannot make the details
 * editor or DesignSpec compiler throw on required nested arrays.
 */
function parsePersistedDesignBrief(value: unknown): DesignBrief | null {
  if (value === null || value === undefined) return null;

  let candidate: unknown;
  try {
    // Clone as well as normalize to plain JSON data. This prevents later Immer
    // edits from mutating an object still owned by a caller/test fixture.
    candidate = JSON.parse(JSON.stringify(value));
  } catch {
    return null;
  }
  if (!isRecord(candidate) || candidate.version !== 2) return null;

  const constraints = candidate.constraints;
  const front = candidate.front;
  const back = candidate.back;
  const references = candidate.references;
  if (
    !isRecord(constraints) ||
    !isRecord(front) ||
    !isRecord(back) ||
    !isRecord(references) ||
    !isRecord(constraints.color)
  ) {
    return null;
  }

  const requiredLists = [
    constraints.mustHave,
    constraints.avoid,
    constraints.comfort,
    constraints.budget,
    constraints.mobility,
    constraints.bodyConcerns,
    constraints.color.preferred,
    constraints.color.rejected,
    candidate.rejectedIdeas,
    candidate.openQuestions,
    front.sleeveAddons,
    front.mood,
    front.details,
    back.details,
    references.front,
    references.back,
    references.inspiration,
  ];
  if (!requiredLists.every(isStringArray)) return null;

  const optionalLists = [
    candidate.clearedFields,
    constraints.color.accents,
    constraints.coverage,
    constraints.support,
    constraints.opacity,
    constraints.accessibility,
    constraints.cultural,
    constraints.construction,
    constraints.referenceGuidance,
    isRecord(candidate.event) ? candidate.event.wearerContext : undefined,
  ];
  if (optionalLists.some(list => list !== undefined && !isStringArray(list))) {
    return null;
  }
  if (
    candidate.activeConcept !== undefined &&
    (!isRecord(candidate.activeConcept) ||
      !isRecord(candidate.activeConcept.visualDecisions))
  ) {
    return null;
  }
  if (
    references.roles !== undefined &&
    (!Array.isArray(references.roles) ||
      references.roles.some(
        role =>
          !isRecord(role) ||
          typeof role.url !== "string" ||
          !["front", "back", "inspiration"].includes(String(role.side)) ||
          typeof role.role !== "string"
      ))
  ) {
    return null;
  }

  return candidate as unknown as DesignBrief;
}

export interface CreatePageStore extends CreatePageState {
  /**
   * Monotonic ownership token for async create-page work. Unlike the auth
   * identity epoch, this also changes when the same account opens a different
   * dress, restores a saved canvas, or starts over.
   */
  projectActionEpoch: number;
  advanceProjectActionEpoch: () => void;

  // Actions
  // Agent actions
  addMessage: (message: Omit<AgentMessage, "id" | "timestamp">) => void;
  removeMessage: (messageId: string) => void;
  updateMessageContent: (messageId: string, content: string) => void;
  setMessagePresets: (messageId: string, presets: PresetOption[]) => void;
  setMessageWidgets: (messageId: string, widgets: AgentWidget[]) => void;
  setMessageSources: (
    messageId: string,
    sources: AgentMessage["sources"] | undefined
  ) => void;
  setMessageThinkingTrace: (
    messageId: string,
    steps: AgentThinkingTraceStep[]
  ) => void;
  markWidgetSelection: (
    messageId: string,
    selectedLabel: string,
    choice?: DesignerChoice
  ) => void;
  setTyping: (isTyping: boolean) => void;
  setThinkingStatus: (status: string | null) => void;
  setThinkingTrace: (steps: AgentThinkingTraceStep[]) => void;
  setPresetsLoading: (loading: boolean) => void;
  setAgentError: (error: string | null) => void;
  setExactSessionRestoreError: (error: string | null) => void;
  clearConversation: () => void;
  restoreMessages: (messages: AgentMessage[]) => void;
  setPhase: (phase: AgentPhase) => void;
  setPresets: (presets: PresetOption[]) => void;
  setSessionId: (sessionId: string | null) => void;
  setSessionVersions: (versions: {
    sessionVersion: number;
    messageVersion: number;
  }) => void;
  setAgentBrainState: (state: {
    designBrief?: DesignBrief | null;
    activeConcept?: DressConcept | null;
    actionPlan?: AgentActionPlan | null;
  }) => void;
  updateDesignDetail: (field: EditableDesignDetailField, value: string) => void;
  stageBackChoice: (value: string) => void;
  setActiveRequestController: (controller: AbortController | null) => void;
  abortActiveRequest: () => void;
  setEntryTransition: (transition: "idle" | "starting" | "settled") => void;
  beginCustomEntryChat: (composerSeed: string) => void;
  clearPendingComposerSeed: () => void;
  setPendingComposerSeed: (composerSeed: string | null) => void;
  setPendingComposerRecovery: (recovery: AgentComposerRecovery | null) => void;
  setPendingSubmittedMessage: (message: string | null) => void;

  // Form actions
  updateFormData: (data: Partial<CreateFormData>) => void;
  resetForm: () => void;
  setValidationError: (show: boolean) => void;
  setUploadedFrontImages: (files: File[]) => void;
  setUploadedBackImages: (files: File[]) => void;
  clearUploadedImages: () => void;

  // Navigation actions
  setStep: (step: number) => void;
  nextStep: () => void;
  prevStep: () => void;
  approveFrontDesign: () => void;
  setBackDesignMode: () => void;

  // Image generation actions
  generateStart: (
    currentStep: number,
    isChatMode?: boolean,
    options?: { preserveExistingPreviews?: boolean }
  ) => void;
  generateFrontSuccess: (
    payload: {
      variations: Array<{ imageUrl: string; imagePath: string | null }>;
      designSpec: RenderedDesignSpec;
      formUpdates?: Partial<
        Pick<
          CreateFormData,
          "sleeve" | "length" | "neckline" | "silhouette" | "fabric" | "color"
        >
      >;
      warnings?: Array<{ variation: number; reason: string; error?: string }>;
      partial_failure?: boolean;
    },
    isChatMode?: boolean
  ) => void;
  generateBackSuccess: (
    payload: {
      variations: Array<{ imageUrl: string; imagePath: string | null }>;
      designSpec: RenderedDesignSpec;
      warnings?: Array<{ variation: number; reason: string; error?: string }>;
      partial_failure?: boolean;
    },
    isChatMode?: boolean
  ) => void;
  generateError: (error: string) => void;
  markFailedSignalConsumed: () => void;
  setGenerationPhase: (phase: "idle" | "front" | "back" | "complete") => void;
  setProgress: (progress: number) => void;
  setGenerationTiming: (timing: {
    startTime: number | null;
    estimatedTime: number | null;
  }) => void;
  setGenerationBlocked: (blocked: boolean, cooldownTime: number) => void;
  setLoading: (loading: boolean) => void;

  // Variation actions
  selectFrontVariation: (index: number) => void;
  selectBackVariation: (index: number) => void;
  setSelectingVariation: (selecting: boolean) => void;

  // Modification actions
  modifyStart: (side?: "front" | "back") => void;
  modifyError: (error: string) => void;
  setModifyInput: (show: boolean) => void;
  setModifyPrompt: (prompt: string) => void;
  setIsModifying: (isModifying: boolean) => void;
  setCurrentView: (view: "front" | "back") => void;
  setModificationMode: (isModificationMode: boolean) => void;
  modifySuccess: (payload: {
    imageUrl: string;
    imagePath: string | null;
    isFront: boolean;
    designSpec?: RenderedDesignSpec;
  }) => void;
  modifyFrontSuccess: (payload: {
    variations: Array<{ imageUrl: string; imagePath: string | null }>;
    designSpec: RenderedDesignSpec;
  }) => void;
  modifyBackSuccess: (payload: {
    variations: Array<{ imageUrl: string; imagePath: string | null }>;
    designSpec: RenderedDesignSpec;
  }) => void;

  // Ticket actions
  setTicketId: (ticketId: string | null) => void;
  setVariationTicketId: (index: number, ticketId: string) => void;
  submitStart: () => void;
  submitSuccess: () => void;
  submitError: (error: string) => void;
  clearSubmissionError: () => void;
  setQuoteRequiresEmailConfirmation: (required: boolean) => void;

  // Credit actions
  /**
   * `stepIndex` is the step the visitor was ON when the wall was raised.
   * Callers that revert the step first (the 402 handlers) must pass it, or the
   * impression is recorded against the reverted step instead of the one the
   * customer actually hit the wall on.
   */
  setShowCreditModal: (
    context?: NonNullable<CreditModalContext>,
    stepIndex?: number
  ) => void;
  hideCreditModal: () => void;
  setShowCreditCheckout: (config: any) => void;
  hideCreditCheckout: () => void;
  setShowCreditSuccess: (data: {
    credits: number;
    transactionId: string;
  }) => void;
  hideCreditSuccess: () => void;
  // Note: setCreditBalance and setCreditCheckLoading removed - use useCredits() hook
  setCreditModalDismissed: (dismissed: boolean) => void;
  setCreditPurchaseLoading: (loading: boolean) => void;

  // Feedback modal actions
  setShowFeedbackModal: () => void;
  hideFeedbackModal: () => void;

  // Account gate shown before an anonymous visitor requests a free quote
  setShowQuoteAccountModal: (show: boolean) => void;

  // Start Over confirmation modal
  setShowStartOverConfirm: (show: boolean) => void;

  // Error actions
  setError: (error: string | null) => void;
  clearError: () => void;
  clearWarnings: () => void;

  // Restoration actions
  restoreImageState: (payload: {
    formData?: CreateFormData;
    prompt?: string;
    color?: string;
    colorHex?: string;
    length?: string;
    sleeve?: string;
    sleeveAddons?: string[];
    neckline?: string;
    waistline?: string;
    silhouette?: string;
    fabric?: string;
    generateBack?: boolean;
    backPrompt?: string;
    uploadedFrontImages?: File[];
    uploadedBackImages?: File[];
    isFrontUploadPreset?: boolean;
    isBackUploadPreset?: boolean;
    imageUrl?: string | null;
    imagePath?: string | null;
    backImageUrl?: string | null;
    backImagePath?: string | null;
    originalImageUrl?: string | null;
    originalImagePath?: string | null;
    frontDesignSpec?: unknown;
    originalBackImageUrl?: string | null;
    originalBackImagePath?: string | null;
    backDesignSpec?: unknown;
    originalFrontDesignSpec?: unknown;
    originalBackDesignSpec?: unknown;
    frontVariations?: PersistedGeneratedImageVariation[] | null;
    backVariations?: PersistedGeneratedImageVariation[] | null;
    selectedFrontVariationIndex?: number | null;
    selectedBackVariationIndex?: number | null;
    currentStep?: number;
    ticketId?: string | null;
    modifyPrompt?: string;
    showModifyInput?: boolean;
    currentView?: "front" | "back";
    originalPresetType?: "preset" | "custom" | "upload" | null;
    designBrief?: unknown;
    chatHistory?: SerializableAgentMessage[];
    presets?: SerializablePresetOption[];
    restoreChatHistory?: boolean; // Flag to indicate if chat history should be restored (only from SavePoint)
    restorePresets?: boolean; // Flag to indicate if presets should be restored (only from SavePoint)
    agentSessionId?: string | null;
    agentSessionVersion?: number;
    agentMessageVersion?: number;
    agentPhase?: AgentPhase;
    /**
     * When a scoped SavePoint overlays the same server-restored session, keep
     * the server's authoritative version counters instead of rolling them back
     * to the browser snapshot.
     */
    preserveAgentSessionIdentity?: boolean;
    pendingSubmittedMessage?: string | null;
    pendingSubmittedMessageId?: string | null;
    pendingComposerSeed?: string | null;
    pendingComposerRecovery?: AgentComposerRecovery | null;
  }) => void;
  restoreVariations: (payload: {
    frontVariations: PersistedGeneratedImageVariation[] | null;
    backVariations: PersistedGeneratedImageVariation[] | null;
    selectedFrontVariationIndex: number | null;
    selectedBackVariationIndex: number | null;
    ticketId: string | null;
    imageUrl: string | null;
    imagePath: string | null;
    backImageUrl: string | null;
    backImagePath: string | null;
    frontDesignSpec?: unknown;
    backDesignSpec?: unknown;
  }) => void;

  // Reset action
  resetAll: () => void;
}

export const useCreatePageStore = create<CreatePageStore>()(
  immer((set, get, api) => ({
    ...createInitialState(),
    projectActionEpoch: 0,
    advanceProjectActionEpoch: () =>
      set((state: CreatePageStore) => {
        state.projectActionEpoch += 1;
      }),
    ...createFormSlice(set, get, api),
    ...createGenerationSlice(set, get, api),
    ...createUISlice(set, get, api),
    ...createTicketSlice(set, get, api),
    ...createAgentSlice(set, get, api),
    // Restoration actions (cross-slice, so defined here)
    restoreImageState: payload =>
      set((state: CreatePageStore) => {
        const replacesAgentProject =
          "restoreChatHistory" in payload || "restorePresets" in payload;
        const matchingRestoredSession =
          payload.preserveAgentSessionIdentity &&
          Boolean(payload.agentSessionId) &&
          state.agent.sessionId === payload.agentSessionId
            ? {
                sessionId: state.agent.sessionId,
                sessionVersion: state.agent.sessionVersion,
                messageVersion: state.agent.messageVersion,
              }
            : null;

        // A restored canvas replaces the project that owned any outstanding
        // async render. Advance before applying the snapshot so a late result
        // from the previous dress cannot write into this one.
        state.projectActionEpoch += 1;

        // Ticket/gallery and SavePoint callers explicitly supply the restore
        // flags. That makes them authoritative project boundaries, including
        // when the incoming transcript or quick-action list is empty. Reset
        // all project-owned agent state before applying the new snapshot so an
        // empty field cannot inherit conversation/planner state from the dress
        // that was previously open in this long-lived client store.
        //
        // Exact server-session hydration intentionally omits these flags. It
        // already resets the whole store and restores messages/brain state
        // before calling restoreImageState for the reconciled canvas, so this
        // boundary must remain opt-in rather than running for every partial
        // image-state patch.
        if (replacesAgentProject) {
          state.agent.activeRequestController?.abort();
          state.agent.activeRequestController = null;
          state.agent.conversation.messages = [];
          state.agent.conversation.isTyping = false;
          state.agent.conversation.thinkingStatus = null;
          state.agent.conversation.thinkingTrace = [];
          state.agent.conversation.presetsLoading = false;
          state.agent.conversation.error = null;
          state.agent.phase = initialState.agent.phase;
          state.agent.presets = [];
          state.agent.sessionId = null;
          state.agent.sessionVersion = 0;
          state.agent.messageVersion = 0;
          state.agent.designBrief = null;
          state.agent.activeConcept = null;
          state.agent.actionPlan = null;
          state.agent.entryTransition = "idle";
          state.agent.entryChatActive = false;
          state.agent.pendingComposerSeed = null;
          state.agent.pendingComposerRecovery = null;
          state.agent.pendingSubmittedMessage = null;
          state.agent.pendingSubmittedMessageId = null;
          state.agent.exactSessionRestoreError = null;
        }

        if (replacesAgentProject) {
          state.agent.sessionId =
            matchingRestoredSession?.sessionId ??
            payload.agentSessionId ??
            null;
          state.agent.sessionVersion =
            matchingRestoredSession?.sessionVersion ??
            payload.agentSessionVersion ??
            0;
          state.agent.messageVersion =
            matchingRestoredSession?.messageVersion ??
            payload.agentMessageVersion ??
            0;
          state.agent.phase = payload.agentPhase ?? initialState.agent.phase;
        }

        // Restore form data
        if (payload.formData) {
          state.form.data = payload.formData;
          state.form.hasPrompt = calculateHasPrompt(payload.formData);
        } else {
          const restoredFormData = {
            prompt: payload.prompt || "",
            color: payload.color || "",
            colorHex: payload.colorHex || "",
            length: payload.length || "",
            sleeve: payload.sleeve || "",
            sleeveAddons: payload.sleeveAddons || [],
            neckline: payload.neckline || "",
            waistline: payload.waistline || "",
            silhouette: payload.silhouette || "",
            fabric: payload.fabric || "",
            generateBack: payload.generateBack || false,
            backPrompt: payload.backPrompt || "",
            uploadedFrontImages: payload.uploadedFrontImages || [],
            uploadedBackImages: payload.uploadedBackImages || [],
            isFrontUploadPreset: payload.isFrontUploadPreset || false,
            isBackUploadPreset: payload.isBackUploadPreset || false,
          };
          state.form.data = restoredFormData;
          state.form.hasPrompt = calculateHasPrompt(
            restoredFormData as CreateFormData
          );
        }

        // Restore image state
        if (payload.imageUrl !== undefined) {
          state.generation.images.front.url = payload.imageUrl;
        }
        if (payload.imagePath !== undefined) {
          state.generation.images.front.path = payload.imagePath;
        }
        if (payload.frontDesignSpec !== undefined) {
          state.generation.images.front.designSpec = parseDesignSpecSnapshot(
            payload.frontDesignSpec
          );
        }
        if (payload.backImageUrl !== undefined) {
          state.generation.images.back.url = payload.backImageUrl;
        }
        if (payload.backImagePath !== undefined) {
          state.generation.images.back.path = payload.backImagePath;
        }
        if (payload.backDesignSpec !== undefined) {
          state.generation.images.back.designSpec = parseDesignSpecSnapshot(
            payload.backDesignSpec
          );
        }
        if (payload.originalImageUrl !== undefined) {
          state.generation.images.front.original.url = payload.originalImageUrl;
        }
        if (payload.originalImagePath !== undefined) {
          state.generation.images.front.original.path =
            payload.originalImagePath;
        }
        if (payload.originalFrontDesignSpec !== undefined) {
          state.generation.images.front.original.designSpec =
            parseDesignSpecSnapshot(payload.originalFrontDesignSpec);
        }
        if (payload.originalBackImageUrl !== undefined) {
          state.generation.images.back.original.url =
            payload.originalBackImageUrl;
        }
        if (payload.originalBackImagePath !== undefined) {
          state.generation.images.back.original.path =
            payload.originalBackImagePath;
        }
        if (payload.originalBackDesignSpec !== undefined) {
          state.generation.images.back.original.designSpec =
            parseDesignSpecSnapshot(payload.originalBackDesignSpec);
        }
        if (payload.frontVariations !== undefined) {
          state.generation.images.variations.front = restoreGeneratedVariations(
            payload.frontVariations
          );
        }
        if (payload.backVariations !== undefined) {
          state.generation.images.variations.back = restoreGeneratedVariations(
            payload.backVariations
          );
        }
        if (payload.selectedFrontVariationIndex !== undefined) {
          state.generation.images.selected.frontIndex =
            payload.selectedFrontVariationIndex;
        }
        if (payload.selectedBackVariationIndex !== undefined) {
          state.generation.images.selected.backIndex =
            payload.selectedBackVariationIndex;
        }

        // Older save points may have per-variation provenance but no explicit
        // active/original field. Reattach only on an exact URL/path match;
        // otherwise history remains unknown rather than inheriting the draft.
        if (
          payload.frontDesignSpec === undefined &&
          (payload.imageUrl !== undefined || payload.imagePath !== undefined)
        ) {
          state.generation.images.front.designSpec = findRenderedVariationSpec(
            state.generation.images.variations.front,
            payload.imageUrl,
            payload.imagePath
          );
        }
        if (
          payload.backDesignSpec === undefined &&
          (payload.backImageUrl !== undefined ||
            payload.backImagePath !== undefined)
        ) {
          state.generation.images.back.designSpec = findRenderedVariationSpec(
            state.generation.images.variations.back,
            payload.backImageUrl,
            payload.backImagePath
          );
        }
        if (
          payload.originalFrontDesignSpec === undefined &&
          (payload.originalImageUrl !== undefined ||
            payload.originalImagePath !== undefined)
        ) {
          state.generation.images.front.original.designSpec =
            findRenderedVariationSpec(
              state.generation.images.variations.front,
              payload.originalImageUrl,
              payload.originalImagePath
            );
        }
        if (
          payload.originalBackDesignSpec === undefined &&
          (payload.originalBackImageUrl !== undefined ||
            payload.originalBackImagePath !== undefined)
        ) {
          state.generation.images.back.original.designSpec =
            findRenderedVariationSpec(
              state.generation.images.variations.back,
              payload.originalBackImageUrl,
              payload.originalBackImagePath
            );
        }

        // Restore navigation state
        if (payload.currentStep !== undefined) {
          state.form.currentStep = payload.currentStep;
        }

        // Restore ticket state
        if (payload.ticketId !== undefined) {
          state.ticket.id = payload.ticketId;
        }

        // Restore chat history (only from SavePoint, not from ticket/gallery prefill)
        if (payload.restoreChatHistory) {
          // Convert serialized messages (ISO string timestamps) back to AgentMessage objects (Date timestamps)
          state.agent.conversation.messages = (payload.chatHistory ?? []).map(
            msg => ({
              id: msg.id,
              role: msg.role,
              content: msg.content,
              timestamp: new Date(msg.timestamp),
              imageUrl: msg.imageUrl,
              imageUrls: msg.imageUrls,
              imageSide: msg.imageSide,
              selectedIndex: msg.selectedIndex,
              hidden: msg.hidden,
              presets: msg.presets?.map(preset => ({
                label: preset.label,
                action: preset.action,
                actionId: preset.actionId,
                presetType: preset.presetType,
                data: preset.data,
                actionData: preset.actionData,
              })),
              widgets: msg.widgets,
              sources: msg.sources,
              thinkingTrace: msg.thinkingTrace,
            })
          );
          if (state.agent.conversation.messages.length > 0) {
            state.agent.entryTransition = "settled";
          }
        }

        // Restore presets (only from SavePoint, not from ticket/gallery prefill)
        if (payload.restorePresets) {
          // Restore presets to agent state
          state.agent.presets = (payload.presets ?? []).map(preset => ({
            label: preset.label,
            action: preset.action,
            actionId: preset.actionId,
            presetType: preset.presetType,
            data: preset.data,
            actionData: preset.actionData,
          }));
        }

        // Presets are restored from SavePoint via quick_action_buttons

        // Restore modification state
        // Only restore if modifyPrompt is truthy (not empty string, undefined, or null)
        if (payload.modifyPrompt) {
          state.ui.modification.prompt = payload.modifyPrompt;
        }
        if (payload.showModifyInput !== undefined) {
          state.ui.modification.showInput = payload.showModifyInput;
        }
        if (payload.currentView !== undefined) {
          state.ui.view = payload.currentView;
        }
        if (payload.originalPresetType !== undefined) {
          state.ui.modification.originalPresetType = payload.originalPresetType;
        }
        if ("designBrief" in payload) {
          state.agent.designBrief = parsePersistedDesignBrief(
            payload.designBrief
          );
          if (replacesAgentProject) {
            state.agent.activeConcept =
              state.agent.designBrief?.activeConcept ?? null;
          }
        }
        if (payload.pendingSubmittedMessage !== undefined) {
          const pendingMessage =
            payload.pendingSubmittedMessage?.trim() || null;
          state.agent.pendingSubmittedMessage = pendingMessage;
          state.agent.pendingSubmittedMessageId = pendingMessage
            ? payload.pendingSubmittedMessageId?.trim() ||
              createPendingSubmittedMessageId(
                JSON.stringify([
                  pendingMessage,
                  payload.ticketId ?? null,
                  payload.imagePath ?? null,
                  payload.formData?.prompt ?? null,
                  payload.chatHistory?.length ?? 0,
                ])
              )
            : null;
        }
        if (payload.pendingComposerRecovery !== undefined) {
          state.agent.pendingComposerRecovery =
            payload.pendingComposerRecovery ?? null;
          state.agent.pendingComposerSeed = payload.pendingComposerRecovery
            ? payload.pendingComposerSeed === undefined
              ? payload.pendingComposerRecovery.message.trim() || null
              : payload.pendingComposerSeed?.trim() || null
            : null;
        }
      }),
    restoreVariations: payload =>
      set((state: CreatePageStore) => {
        state.generation.images.variations.front = restoreGeneratedVariations(
          payload.frontVariations
        );
        state.generation.images.variations.back = restoreGeneratedVariations(
          payload.backVariations
        );
        state.generation.images.selected.frontIndex =
          payload.selectedFrontVariationIndex;
        state.generation.images.selected.backIndex =
          payload.selectedBackVariationIndex;
        state.generation.images.front.url = payload.imageUrl;
        state.generation.images.front.path = payload.imagePath;
        state.generation.images.front.designSpec =
          payload.frontDesignSpec !== undefined
            ? parseDesignSpecSnapshot(payload.frontDesignSpec)
            : findRenderedVariationSpec(
                state.generation.images.variations.front,
                payload.imageUrl,
                payload.imagePath
              );
        state.generation.images.back.url = payload.backImageUrl;
        state.generation.images.back.path = payload.backImagePath;
        state.generation.images.back.designSpec =
          payload.backDesignSpec !== undefined
            ? parseDesignSpecSnapshot(payload.backDesignSpec)
            : findRenderedVariationSpec(
                state.generation.images.variations.back,
                payload.backImageUrl,
                payload.backImagePath
              );
        state.ticket.id = payload.ticketId;
      }),
    // Reset action
    resetAll: () =>
      set((state: CreatePageStore) => {
        const nextProjectActionEpoch = state.projectActionEpoch + 1;
        Object.assign(state, createInitialState());
        state.projectActionEpoch = nextProjectActionEpoch;
      }),
  }))
);

// Re-export types for backward compatibility
export type { CreatePageState, CreatePageAction } from "./types";
